import { useState, useEffect, useRef } from "react";
import { setPageDetail } from "@/hooks/usePageTitle";
import axios from "axios";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Calendar, User, FileText, Vote, Activity, TrendingUp, TrendingDown, Users, Globe, DollarSign, Download, FileCode, Network, Tag } from "lucide-react";
import NewsCard from "@/components/NewsCard";
import PassageForecast from "@/components/PassageForecast";
import { WatchButton } from "@/components/WatchButton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillDetail = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLegislator = location.state?.fromLegislator;
  const [bill, setBill] = useState(null);
  const [votes, setVotes] = useState([]);
  const [lifecycleVotes, setLifecycleVotes] = useState([]);
  const [voteMembers, setVoteMembers] = useState(null);
  const [votesLoading, setVotesLoading] = useState(true);
  const [votesError, setVotesError] = useState(false);
  const [alignment, setAlignment] = useState(null);
  const [textVersions, setTextVersions] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const handleBack = () => {
    if (fromLegislator) {
      navigate(`/legislator/${fromLegislator}`);
    } else {
      navigate(-1);
    }
  };

  const analysisPollRef = useRef(0);

  useEffect(() => {
    fetchBillDetails();
    fetchAnalysis();
    return () => { analysisPollRef.current += 1; };
  }, [billId]);

  const fetchBillDetails = async () => {
    try {
      setLoading(true);
      // Render the page as soon as the fast endpoints return; AI analysis is
      // fetched in parallel on its own state so it never blocks page render.
      const [billRes, votesRes, actionsRes, textRes] = await Promise.all([
        axios.get(`${API}/bills/${billId}`),
        axios.get(`${API}/bills/${billId}/votes`),
        axios.get(`${API}/bills/${billId}/actions`),
        axios.get(`${API}/bills/${billId}/text`).catch(() => ({ data: [] })),
      ]);
      setBill(billRes.data);
      setPageDetail(billRes.data?.billNumber);
      setVotes(votesRes.data);
      setActions(actionsRes.data);
      setTextVersions(textRes.data || []);
    } catch (error) {
      toast.error("Failed to load bill details");
    } finally {
      setLoading(false);
    }
    // Roll-call data can take a while on a cold cache (20+ rolls for big
    // bills), load it on its own so it never blocks or silently fails the page.
    fetchRollCalls();
  };

  const fetchRollCalls = async () => {
    setVotesLoading(true);
    setVotesError(false);
    try {
      const vm = await axios.get(`${API}/bills/${billId}/vote-members`, { timeout: 120000 });
      setVoteMembers(vm.data || { hasVotes: false, rolls: [] });
      const [lc, al] = await Promise.all([
        axios.get(`${API}/bills/${billId}/lifecycle-votes`, { timeout: 60000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/bills/${billId}/alignment`, { timeout: 120000 }).catch(() => ({ data: { hasData: false } })),
      ]);
      setLifecycleVotes(lc.data || []);
      setAlignment(al.data || { hasData: false });
    } catch (e) {
      setVotesError(true);
    } finally {
      setVotesLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    // Try up to 15 times (~45s total) with 3s gaps; backend returns 202 while
    // generation is in flight. We stop polling once we get a real analysis,
    // on error, or on the last attempt.
    let attempt = 0;
    const maxAttempts = 36;
    const myPoll = ++analysisPollRef.current;
    setAnalysisLoading(true);
    const poll = async () => {
      if (analysisPollRef.current !== myPoll) return;
      attempt += 1;
      try {
        const resp = await axios.get(`${API}/bills/${billId}/analysis`, {
          timeout: 15000,
          validateStatus: (s) => s === 200 || s === 202,
        });
        if (resp.status === 200 && resp.data && !resp.data.status) {
          setAnalysis(resp.data);
          setAnalysisLoading(false);
          return;
        }
        if (attempt >= maxAttempts) {
          setAnalysisLoading(false);
          return;
        }
        setTimeout(poll, 5000);
      } catch (e) {
        if (attempt >= maxAttempts) {
          setAnalysisLoading(false);
          return;
        }
        setTimeout(poll, 8000);
      }
    };
    poll();
  };
  
  const generateBillAnalysis = (billData) => {
    // Generate contextual analysis based on bill subjects and title
    const subjects = billData.subjects || [];
    const title = billData.title.toLowerCase();
    const summary = billData.summary.toLowerCase();
    
    // Determine key focus areas
    const isHealthcare = subjects.some(s => s.toLowerCase().includes('health')) || title.includes('health') || title.includes('medicare');
    const isEconomic = subjects.some(s => s.toLowerCase().includes('econom')) || title.includes('tax') || title.includes('economic');
    const isEnvironment = subjects.some(s => s.toLowerCase().includes('environment') || s.toLowerCase().includes('climate'));
    const isEducation = subjects.some(s => s.toLowerCase().includes('education'));
    const isSecurity = subjects.some(s => s.toLowerCase().includes('security') || s.toLowerCase().includes('defense'));
    
    return {
      pros: [
        isHealthcare ? "Expands healthcare access and coverage for citizens" : 
        isEconomic ? "Aims to strengthen economic growth and create jobs" :
        isEnvironment ? "Addresses climate change and environmental sustainability" :
        isEducation ? "Improves educational opportunities and resources" :
        isSecurity ? "Enhances national security and public safety" :
        "Addresses important policy priorities",
        
        "Received bipartisan consideration in Congress",
        "Aligns with constituent needs and concerns",
        "Based on evidence and expert testimony"
      ],
      cons: [
        isEconomic ? "May increase federal spending and budget deficit" :
        "Could face implementation challenges",
        
        "Some provisions may require further refinement",
        "Potential for unintended consequences",
        "May not go far enough to address core issues"
      ],
      impact: {
        citizens: isHealthcare ? "Direct impact on healthcare access, costs, and quality of care for millions of Americans" :
                  isEconomic ? "Affects household income, job availability, and economic opportunity" :
                  isEducation ? "Influences educational outcomes, student debt, and access to learning" :
                  "Affects daily lives through changes in services, regulations, and rights",
        
        country: isSecurity ? "Strengthens national security posture and defense capabilities" :
                 isEconomic ? "Influences GDP growth, employment rates, and fiscal health" :
                 isEnvironment ? "Addresses long-term environmental sustainability and climate resilience" :
                 "Shapes national priorities and policy direction",
        
        political: "Reflects ongoing policy debates and party priorities; may influence future elections and legislative agenda",
        
        economic: isEconomic ? "Direct fiscal impact through spending, taxation, or economic regulation" :
                  "Indirect economic effects through changes in markets, employment, or business conditions",
        
        geographic: "Implementation varies by state and region; some areas may see greater impact than others",
        
        social: isHealthcare || isEducation ? "Affects quality of life, social mobility, and access to essential services" :
                "Influences social policies, community programs, and civil rights protections"
      }
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      Enacted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      "Passed House": "bg-[#2F6BFF]/15 text-blue-300 border-[#2F6BFF]/30",
      "Passed Senate": "bg-purple-500/15 text-purple-300 border-purple-500/30",
      Introduced: "bg-white/5 text-white/60 border-white/15",
      Failed: "bg-[#FF3B3B]/15 text-rose-300 border-[#FF3B3B]/30",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getActionIcon = (actionType) => {
    const icons = {
      Introduced: "🎯",
      Referred: "📋",
      Reported: "📊",
      Passed: "✅",
      Received: "📩",
      Enacted: "⚖️",
    };
    return icons[actionType] || "📄";
  };

  /** Plain-English explanation surfaced on hover for each lifecycle node.
   *  We first try to match common Congress.gov action phrases inside the raw
   *  `description`, then fall back to the canonical `actionType` bucket. */
  const getActionExplanation = (actionType, description) => {
    const t = (description || "").toLowerCase();
    if (t.includes("became public law") || t.includes("signed by president"))
      return "🎉 Signed into law, the bill is now binding federal law and will be enforced as written.";
    if (t.includes("presented to president"))
      return "Sent to the President for signature. The President has 10 days to sign, veto, or do nothing (which can also become law if Congress is in session).";
    if (t.includes("vetoed"))
      return "🛑 The President rejected the bill. To still become law it now needs a 2/3 override vote in both chambers.";
    if (t.includes("on passage passed") || (t.includes("agreed to") && t.includes("yeas")))
      return "Members held a recorded floor vote and a majority voted yes. The bill clears this chamber and moves to the other one.";
    if (t.includes("passed senate") || (t.includes("passed/agreed to in senate")))
      return "The full Senate voted yes. The bill now heads to the House (or to the President, if it has already cleared the House).";
    if (t.includes("passed house") || t.includes("passed/agreed to in house"))
      return "The full House voted yes. The bill now heads to the Senate (or to the President, if it has already cleared the Senate).";
    if (t.includes("received in the senate"))
      return "The Senate has officially received the bill from the House. It usually waits at the desk until the majority leader schedules action.";
    if (t.includes("received in the house"))
      return "The House has officially received the bill from the Senate. It typically waits to be referred to a committee.";
    if (t.includes("placed on") && t.includes("calendar"))
      return "The bill is on the chamber's daily calendar, eligible to be called up for a floor vote at the leadership's discretion.";
    if (t.includes("ordered to be reported") || t.includes("reported by"))
      return "The committee finished its work and is sending the bill back to the full chamber for floor consideration. Major hurdle cleared.";
    if (t.includes("hearings held"))
      return "The committee held a public hearing, testimony from witnesses, agency officials, and stakeholders. Often a precursor to a markup.";
    if (t.includes("markup"))
      return "Committee members debated and amended the text line-by-line. After markup, the committee usually votes to report or table.";
    if (t.includes("referred to") || t.includes("referral"))
      return "The bill was assigned to one or more committees. Most bills die here without ever getting a hearing.";
    if (t.includes("introduced") || t.includes("introduction"))
      return "A member formally filed the bill, kicking off the legislative process. The next step is committee referral.";
    if (t.includes("read") && (t.includes("twice") || t.includes("first time") || t.includes("second time")))
      return "Procedural reading on the floor. Required by chamber rules before a bill can be referred or considered further.";
    if (t.includes("considered") && t.includes("agreed to"))
      return "Considered and agreed to, typically a quick non-controversial passage by unanimous consent or voice vote.";
    if (t.includes("motion to") && t.includes("agreed to"))
      return "A procedural motion (often setting up debate rules or limiting time) was approved. Procedure, not the underlying bill itself.";
    if (t.includes("cloture")) return "A successful cloture vote ended Senate debate. The Senate can now move to a final vote on the bill.";
    if (t.includes("conference"))
      return "House and Senate negotiators are reconciling differences between the two chambers' versions before a final identical text is voted on.";
    // Fallback by canonical bucket
    const fallbacks = {
      Introduced: "A member filed the bill in the chamber. This kicks off the legislative process.",
      Referred: "The bill was sent to a committee for review. Most bills die here without a hearing.",
      Reported: "The committee finished its work and is sending the bill to the full chamber for a floor vote.",
      Passed: "The chamber voted yes on the bill. It now heads to the other chamber (or to the President if both have passed it).",
      Received: "The bill arrived in the other chamber after passing the originating one. It typically waits to be referred to committee.",
      Enacted: "The bill is now law, signed by the President (or passed over a veto, or became law without a signature).",
    };
    return fallbacks[actionType] || "A legislative action on this bill.";
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading bill details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">Bill not found</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={handleBack}
            className="mb-4 text-white/70 hover:bg-white/5 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {fromLegislator ? "Back to Profile" : "Back"}
          </Button>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 border font-semibold px-3 py-1 text-base">
                  {bill.billNumber}
                </Badge>
                <Badge className={`${getStatusColor(bill.status)} border font-semibold px-3 py-1 text-base`}>
                  {bill.status}
                </Badge>
                <Badge variant="outline" className="border border-white/15 bg-white/5 text-white/70 px-3 py-1 text-base">
                  {bill.congress}th Congress
                </Badge>
                <WatchButton type="bill" refId={billId} label={`${bill.billNumber}, ${bill.title}`} />
              </div>
              <h1 className="text-4xl font-black tracking-tight text-white mb-2" data-testid="bill-title">
                {bill.title}
              </h1>
              {bill.popularTitle && bill.popularTitle !== bill.title && (
                <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 border border-orange-500/40" data-testid="bill-popular-title">
                  <span className="text-xs uppercase tracking-wider text-orange-300/80">Also known as</span>
                  <span className="text-sm font-semibold text-orange-200">{bill.popularTitle}</span>
                </div>
              )}
              <div className="flex items-center gap-6 text-white/55">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="text-base">Introduced: {new Date(bill.introducedDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="text-base">
                    Sponsor: {bill.sponsor.name} ({bill.sponsor.party}-{bill.sponsor.state})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left Column - Bill Summary and Subjects */}
          <div className="lg:col-span-2 space-y-8">
            {/* Passage Forecast (hidden automatically for enacted bills) */}
            <PassageForecast billId={billId} />
            {/* Summary */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Bill Summary</h2>
              </div>
              {analysis && analysis.plainLanguageSummary && (() => {
                // Guard: if the LLM (or its fallback) handed back the bill's
                // title as the "summary", show a useful placeholder instead
                // of restating the title.
                const summary = (analysis.plainLanguageSummary || "").trim();
                const t = (bill?.title || "").trim();
                const titleEcho = summary && t && (
                  summary === t ||
                  summary === t + "." ||
                  summary.toLowerCase() === t.toLowerCase()
                );
                if (titleEcho) {
                  return (
                    <p className="text-base text-gray-500 italic leading-relaxed mb-4" data-testid="bill-summary-prose">
                      Plain-language summary not yet available. Congress.gov
                      hasn't published an official summary for this bill, and
                      our AI couldn't infer one from the title alone. Check
                      back as the bill progresses through committee.
                    </p>
                  );
                }
                return (
                  <p className="text-base text-gray-800 leading-relaxed mb-4" data-testid="bill-summary-prose">
                    {summary}
                  </p>
                );
              })()}
              {!analysis && analysisLoading && (
                <div className="bg-blue-50 border-l-4 border-blue-300 p-4 mb-4 rounded flex items-center gap-3" data-testid="ai-analysis-loading">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-blue-900">Generating AI-powered plain-language summary… (first load ~30s)</p>
                </div>
              )}
              {!analysis && !analysisLoading && (
                <div className="bg-amber-50 border-l-4 border-amber-300 p-4 mb-4 rounded" data-testid="ai-analysis-pending">
                  <p className="text-sm text-amber-900">The AI summary for this bill is still being prepared. It generates in the background, check back in a few minutes.</p>
                </div>
              )}
            </Card>

            {/* Bill Analysis - Pros, Cons & Impact */}
            {analysisLoading && !analysis && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="ai-analysis-loading-card">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Bill Analysis & Impact</h2>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p>WHIP is analyzing this bill. Previously analyzed bills load instantly; a first-time analysis takes about 30 seconds.</p>
                </div>
              </Card>
            )}
            {!analysisLoading && !analysis && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="ai-analysis-pending-card">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Bill Analysis & Impact</h2>
                <p className="text-gray-600">The impact analysis for this bill is still being prepared in the background. Check back in a few minutes, it will appear here automatically once ready.</p>
              </Card>
            )}
            {analysis && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Bill Analysis & Impact</h2>
                
                <Tabs defaultValue="proscons" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="proscons">Pros & Cons</TabsTrigger>
                    <TabsTrigger value="impact">Impact Analysis</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="proscons" className="space-y-6 mt-6">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        <h3 className="text-lg font-semibold text-green-900">Potential Benefits</h3>
                      </div>
                      <ul className="space-y-2">
                        {analysis.pros.map((pro) => (
                          <li key={pro} className="flex items-start gap-2 text-gray-700">
                            <span className="text-green-600 mt-1">✓</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-5 h-5 text-orange-600" />
                        <h3 className="text-lg font-semibold text-orange-900">Potential Concerns</h3>
                      </div>
                      <ul className="space-y-2">
                        {analysis.cons.map((con) => (
                          <li key={con} className="flex items-start gap-2 text-gray-700">
                            <span className="text-orange-600 mt-1">!</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="impact" className="space-y-4 mt-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold text-blue-900">Citizens</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.citizens}</p>
                      </div>
                      
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="w-5 h-5 text-purple-600" />
                          <h4 className="font-semibold text-purple-900">Country</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.country}</p>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-green-900">Economic</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.economic}</p>
                      </div>
                      
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-5 h-5 text-amber-600" />
                          <h4 className="font-semibold text-amber-900">Political</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.political}</p>
                      </div>
                      
                      <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="w-5 h-5 text-teal-600" />
                          <h4 className="font-semibold text-teal-900">Geographic</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.geographic}</p>
                      </div>
                      
                      <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-5 h-5 text-pink-600" />
                          <h4 className="font-semibold text-pink-900">Social</h4>
                        </div>
                        <p className="text-sm text-gray-700">{analysis.impact.social}</p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            )}

            {/* Full Bill Text Download */}
            {textVersions.length > 0 && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="bill-text-card">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="w-6 h-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Full Bill Text</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Download or view the complete legislative text in PDF, formatted HTML, or XML.
                </p>
                <div className="space-y-3">
                  {textVersions.map((version, vIdx) => (
                    <div
                      key={`${version.type || "text"}-${version.date || vIdx}`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
                      data-testid={`bill-text-version-${vIdx}`}
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{version.type || "Bill Text"}</p>
                        {version.date && (
                          <p className="text-xs text-gray-500">
                            {new Date(version.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(version.formats || []).map((f, fIdx) => {
                          const label =
                            f.type === "PDF" ? "PDF" :
                            f.type === "Formatted Text" ? "HTML / Text" :
                            f.type === "Formatted XML" ? "XML" :
                            f.type;
                          const Icon = f.type === "PDF" ? Download : FileCode;
                          return (
                            <a
                              key={fIdx}
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`bill-text-download-${vIdx}-${f.type.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <Button variant="outline" size="sm" className="gap-2">
                                <Icon className="w-4 h-4" />
                                {label}
                              </Button>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Who voted, members of each roll-call grouped by Yea/Nay/Present/Not Voting */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="who-voted-card">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-6 h-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">Who Voted</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Every member's position on the recorded roll-call votes for this bill. Click any
                name to see that legislator's full profile.
              </p>
              {votesLoading ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 flex items-center gap-3" data-testid="who-voted-loading">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  Loading roll-call votes from both chambers, large bills can take up to a minute the first time.
                </div>
              ) : votesError ? (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-900 flex items-center justify-between gap-3" data-testid="who-voted-error">
                  <span>Couldn't load the roll-call votes just now.</span>
                  <Button size="sm" variant="outline" onClick={fetchRollCalls} data-testid="who-voted-retry">Retry</Button>
                </div>
              ) : !voteMembers || !voteMembers.hasVotes ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="who-voted-empty">
                  No recorded votes yet, this bill is still in <strong>{bill?.status || "progress"}</strong>.
                </div>
              ) : (
                <div className="space-y-8">
                  {voteMembers.rolls.map((roll, rIdx) => (
                    <div key={rIdx} data-testid={`roll-call-${rIdx}`}>
                      <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {roll.chamber} Roll Call #{roll.rollCallNumber}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {roll.date && new Date(roll.date).toLocaleDateString()} · {roll.description}
                          </p>
                        </div>
                        <Badge className={
                          roll.result === "Passed"
                            ? "bg-green-100 text-green-800 border-green-200 border"
                            : roll.result === "Failed"
                            ? "bg-red-100 text-red-800 border-red-200 border"
                            : "bg-gray-100 text-gray-800 border-gray-200 border"
                        }>
                          {roll.result}: {roll.totals.yeas}, {roll.totals.nays}
                        </Badge>
                      </div>
                      <div className="grid md:grid-cols-4 gap-4">
                        <VoteBucket
                          title="Yea"
                          accent="bg-green-50 border-green-200"
                          chipClass="bg-green-100 text-green-800 border-green-200"
                          members={roll.members.yea}
                        />
                        <VoteBucket
                          title="Nay"
                          accent="bg-red-50 border-red-200"
                          chipClass="bg-red-100 text-red-800 border-red-200"
                          members={roll.members.nay}
                        />
                        <VoteBucket
                          title="Present"
                          accent="bg-gray-50 border-gray-200"
                          chipClass="bg-gray-100 text-gray-700 border-gray-200"
                          members={roll.members.present}
                        />
                        <VoteBucket
                          title="Not Voting"
                          accent="bg-gray-50 border-gray-200"
                          chipClass="bg-gray-100 text-gray-700 border-gray-200"
                          members={roll.members.notVoting}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Voting History by Lifecycle Stage */}
            {lifecycleVotes.length > 0 && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="lifecycle-votes-card">
                <div className="flex items-center gap-2 mb-2">
                  <Vote className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Voting History by Stage</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Roll-call vote breakdown (Yea / Nay / Present / Not Voting) at each stage of this bill's lifecycle. Click a row to expand its chart.
                </p>
                <div className="space-y-3">
                  {lifecycleVotes.map((stage) => (
                    <LifecycleStageRow key={stage.id} stage={stage} />
                  ))}
                </div>
              </Card>
            )}

            {/* Who aligned with the sponsor on this bill */}
            {alignment && alignment.hasData && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="alignment-card">
                <div className="flex items-center gap-2 mb-2">
                  <Network className="w-6 h-6 text-violet-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Who Aligns With{alignment.sponsor?.name ? ` ${alignment.sponsor.name.replace(/^Rep\. |^Sen\. /, "")}` : " the Sponsor"}
                  </h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Among every member who voted on this bill, these are the legislators who vote
                  with the sponsor most often, and those who vote against them most often, across all other roll-call votes in our cache (minimum {alignment.minShared || 5} shared votes).
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <AlignmentColumn
                    title="Closest Allies"
                    subtitle="Highest historical agreement with sponsor"
                    accent="bg-emerald-50 border-emerald-200"
                    chipClass="bg-emerald-100 text-emerald-800 border-emerald-200"
                    members={alignment.topAllies || []}
                  />
                  <AlignmentColumn
                    title="Strongest Opponents"
                    subtitle="Lowest historical agreement with sponsor"
                    accent="bg-rose-50 border-rose-200"
                    chipClass="bg-rose-100 text-rose-800 border-rose-200"
                    members={alignment.topOpponents || []}
                  />
                </div>
              </Card>
            )}
            {votes.length > 0 && (
              <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
                <div className="flex items-center gap-2 mb-6">
                  <Vote className="w-6 h-6 text-green-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Voting Results</h2>
                </div>
                <div className="space-y-6">
                  {votes.map((vote) => {
                    const voteData = [
                      { name: "Yeas", value: vote.yeas, fill: "#22c55e" },
                      { name: "Nays", value: vote.nays, fill: "#ef4444" },
                      { name: "Present", value: vote.present, fill: "#a3a3a3" },
                      { name: "Not Voting", value: vote.notVoting, fill: "#d4d4d4" },
                    ];

                    return (
                      <div key={vote.id} data-testid={`vote-record-${vote.id}`}>
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{vote.chamber}</h3>
                              <p className="text-sm text-gray-600">{vote.description}</p>
                            </div>
                            <div className="text-right">
                              <Badge
                                className={
                                  vote.result === "Passed"
                                    ? "bg-green-100 text-green-800 border-green-200 border"
                                    : "bg-red-100 text-red-800 border-red-200 border"
                                }
                              >
                                {vote.result}
                              </Badge>
                              <p className="text-sm text-gray-600 mt-1">
                                {new Date(vote.voteDate).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={voteData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="name" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip
                              contentStyle={{ borderRadius: "8px" }}
                            />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                              {voteData.map((entry, index) => (
                                <Cell key={entry.name} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                        {vote.id !== votes[votes.length - 1].id && <Separator className="my-6" />}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Subjects, Legislative Timeline, News */}
          <div className="lg:col-span-1 space-y-8">
            {/* Subjects */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Areas & Subjects</h2>
              {bill.subjects.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="subjects-empty">
                  Congress.gov has not yet assigned policy area or subjects to this bill, this typically happens within a few days of introduction.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {bill.subjects.map((subject, idx) => {
                    // The first subject is the bill's official policy area
                    // (per data_transformer). Link it to the Bills list filter
                    // so users can browse other bills tagged to the same area.
                    const isPolicyArea = idx === 0;
                    if (isPolicyArea) {
                      return (
                        <Link
                          key={subject}
                          to={`/legislation?policyArea=${encodeURIComponent(subject)}`}
                          data-testid={`subject-badge-${idx}`}
                          title={`See all bills tagged ${subject}`}
                        >
                          <Badge
                            className="bg-orange-500/15 text-orange-300 border-orange-500/30 border px-3 py-2 text-sm font-semibold hover:bg-orange-500/25 hover:border-orange-500/50 transition-colors cursor-pointer"
                          >
                            <Tag className="w-3.5 h-3.5 mr-1.5" />
                            {subject}
                          </Badge>
                        </Link>
                      );
                    }
                    return (
                      <Badge
                        key={subject}
                        className="bg-gray-50 text-gray-700 border-gray-200 border px-3 py-2 text-sm font-medium"
                        data-testid={`subject-badge-${idx}`}
                      >
                        {subject}
                      </Badge>
                    );
                  })}
                </div>
              )}
              {bill.subjects.length > 0 && (
                <p className="text-xs text-gray-500 mt-3">
                  Click the policy area to see other bills tagged to it.
                </p>
              )}
            </Card>

            <Card className="p-6 rounded-xl border border-white/10" style={{ background: "var(--whip-surface)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-6 h-6 text-orange-400" />
                <h2 className="text-2xl font-bold text-white">Legislative Process</h2>
              </div>
              <p className="text-xs text-white/45 mb-5">Hover any step for what it means in plain English.</p>
              <div className="space-y-6 max-h-[560px] overflow-y-auto pr-2 scrollbar-thin" data-testid="legislative-process-scroll">
                {actions.map((action, idx) => (
                  <div key={action.id} className="relative group" data-testid={`action-${action.id}`}>
                    {idx !== actions.length - 1 && (
                      <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-white/10"></div>
                    )}
                    <div className="flex gap-4">
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white text-lg shadow-md"
                        style={{ background: "linear-gradient(135deg,#FF6A00,#FFA500)" }}
                      >
                        {getActionIcon(action.actionType)}
                      </div>
                      <div className="flex-1 pb-6">
                        <div
                          className="rounded-lg p-4 border transition-all"
                          style={{ background: "rgba(255,255,255,0.03)", borderColor: "var(--whip-border)" }}
                        >
                          <p className="font-semibold text-white mb-1">{action.actionType}</p>
                          <p className="text-sm text-white/75 mb-2">{action.description}</p>
                          {/* Layman's-terms expansion, appears on hover. The
                              native `title=` tooltip was removed because it
                              duplicated this inline explanation. */}
                          <p className="hidden group-hover:block text-xs text-orange-300/90 mb-2 italic leading-relaxed border-l-2 border-orange-500/40 pl-2">
                            {getActionExplanation(action.actionType, action.description)}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-white/50">
                            <span>{new Date(action.actionDate).toLocaleDateString()}</span>
                            <Badge variant="outline" className="text-xs border-white/15 text-white/65 bg-white/5">
                              {action.chamber}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {actions.length === 0 && (
                  <p className="text-white/45 text-center py-8">No legislative actions recorded</p>
                )}
              </div>
              {actions.length > 5 && (
                <p className="text-[11px] text-white/35 mt-3" data-testid="legislative-process-scroll-hint">Scroll within the timeline to see all {actions.length} steps.</p>
              )}
            </Card>

            {/* Public news coverage */}
            <NewsCard endpoint={`/bills/${bill.id}/news`} testId="bill-news" />
          </div>
        </div>
      </main>
    </div>
  );
};

function LifecycleStageRow({ stage }) {
  const [open, setOpen] = useState(false);
  const chartData = [
    { name: "Yea", value: stage.yeas, fill: "#22c55e" },
    { name: "Nay", value: stage.nays, fill: "#ef4444" },
    { name: "Present", value: stage.present, fill: "#a3a3a3" },
    { name: "Not Voting", value: stage.notVoting, fill: "#d4d4d4" },
  ];
  const resultClass =
    stage.result === "Passed"
      ? "bg-green-100 text-green-800 border-green-200 border"
      : stage.result === "Failed"
      ? "bg-red-100 text-red-800 border-red-200 border"
      : "bg-gray-100 text-gray-800 border-gray-200 border";

  return (
    <div
      className="border border-gray-200 rounded-lg overflow-hidden"
      data-testid={`lifecycle-stage-${stage.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
        aria-expanded={open}
        data-testid={`lifecycle-toggle-${stage.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900">{stage.stage}</h3>
            <span className="text-xs text-gray-500">
              {stage.chamber}
              {stage.actionDate ? ` · ${new Date(stage.actionDate).toLocaleDateString()}` : ""}
            </span>
          </div>
          {/* Always-visible vote tallies */}
          <div className="flex items-center gap-3 mt-1 text-xs flex-wrap">
            <span className="font-medium text-green-700">Yea {stage.yeas}</span>
            <span className="font-medium text-red-700">Nay {stage.nays}</span>
            <span className="text-gray-600">Present {stage.present}</span>
            <span className="text-gray-500">Not Voting {stage.notVoting}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge className={resultClass}>
            {stage.result}: {stage.yeas}, {stage.nays}
          </Badge>
          <span
            className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          >
            ▾
          </span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/40" data-testid={`lifecycle-chart-${stage.id}`}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: "8px" }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {stage.description && (
            <p className="text-xs text-gray-500 mt-2 italic">{stage.description}</p>
          )}
        </div>
      )}
    </div>
  );
}

function AlignmentColumn({ title, subtitle, accent, chipClass, members }) {
  // Visual parity with `VoteBucket` above: pastel accented container +
  // solid-white rows inside so member names render in high contrast
  // (dark text on white) instead of the washed-out `bg-white/60` look.
  return (
    <div className={`${accent} border rounded-lg p-3`} data-testid={`alignment-col-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <span className="font-semibold text-gray-900">{title}</span>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        <span className={`${chipClass} border px-2 py-0.5 rounded-full text-xs font-bold shrink-0`}>
          {members.length}
        </span>
      </div>
      {members.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Not enough shared votes to rank.</p>
      ) : (
        <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {members.map((m, i) => (
            <li
              key={(m.legislatorId || m.name) + i}
              className="flex items-center justify-between gap-2 bg-white rounded-md px-3 py-2 border border-white"
              data-testid={`alignment-member-${i}`}
            >
              <div className="min-w-0">
                {m.legislatorId ? (
                  <Link
                    to={`/legislator/${m.legislatorId}`}
                    className="font-medium text-sm text-gray-900 hover:text-violet-700 truncate block"
                  >
                    {m.name}
                  </Link>
                ) : (
                  <span className="font-medium text-sm text-gray-900">{m.name}</span>
                )}
                <p className="text-xs text-gray-500">
                  {m.party}-{m.state} · voted <span className="font-medium">{m.votedOnBill}</span> on this bill
                </p>
              </div>
              <div className="text-right shrink-0">
                <span className={`${chipClass} border px-2 py-0.5 rounded-full text-xs font-bold`}>
                  {m.agreementPct.toFixed(1)}%
                </span>
                <p className="text-[10px] text-gray-500 mt-0.5">{m.sharedRolls} shared votes</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VoteBucket({ title, accent, chipClass, members }) {
  const [expanded, setExpanded] = useState(false);
  const showAll = expanded || members.length <= 12;
  const visible = showAll ? members : members.slice(0, 12);
  return (
    <div className={`${accent} border rounded-lg p-3`} data-testid={`vote-bucket-${title.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900">{title}</span>
        <span className={`${chipClass} border px-2 py-0.5 rounded-full text-xs font-bold`}>
          {members.length}
        </span>
      </div>
      {members.length === 0 && <p className="text-xs text-gray-500 italic">None</p>}
      <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {visible.map((m, i) => (
          <li key={(m.legislatorId || m.name) + i} className="text-sm leading-snug">
            {m.legislatorId ? (
              <Link
                to={`/legislator/${m.legislatorId}`}
                className="text-gray-900 hover:text-indigo-700 font-medium"
              >
                {m.name}
              </Link>
            ) : (
              <span className="text-gray-900 font-medium">{m.name}</span>
            )}
            <span className="text-xs text-gray-500 ml-1">({m.party}-{m.state})</span>
          </li>
        ))}
      </ul>
      {members.length > 12 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-medium text-indigo-700 hover:underline"
          data-testid={`vote-bucket-toggle-${title.toLowerCase().replace(' ', '-')}`}
        >
          {expanded ? "Show less" : `Show all ${members.length}`}
        </button>
      )}
    </div>
  );
}

export default BillDetail;
